import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'sonner';
import { Loader2, Printer, Copy, ArrowLeft, ArrowRight } from 'lucide-react';

// iter380 (v14) — Advertiser-facing campaign performance report.
// Admin-authenticated. Professional printable layout (Print / Save PDF)
// + WhatsApp-friendly Copy Summary. Exposes no internal IDs, notes or
// payment amounts. Daily buckets: Sudan time (UTC+2), documented on page.

const API = process.env.REACT_APP_BACKEND_URL;

const T = {
  en: {
    title: 'Sponsored Campaign Report',
    brandSub: 'Wassla Marketplace — Advertising Performance',
    campaign: 'Campaign', advertiser: 'Advertiser', listing: 'Sponsored listing',
    placement: 'Placement', targeting: 'Targeting', period: 'Campaign period',
    status: 'Status', impressions: 'Impressions', clicks: 'Clicks', ctr: 'CTR',
    daily: 'Daily breakdown', date: 'Date', none: '—',
    placements: { homepage: 'Homepage', related_carousel: 'Related listings carousel' },
    statuses: { active: 'Active', scheduled: 'Scheduled', paused: 'Paused', expired: 'Completed' },
    tzNote: 'All dates are shown in Sudan time (Africa/Khartoum).',
    payNote: 'Payment settled outside Wassla.',
    generated: 'Report generated',
    print: 'Print / Save PDF', copy: 'Copy Summary', back: 'Back',
    copied: 'Summary copied', copyFail: 'Copy failed',
    noDaily: 'No activity recorded yet for this campaign.',
  },
  ar: {
    title: 'تقرير الحملة الإعلانية الممولة',
    brandSub: 'سوق وصلة — أداء الإعلانات',
    campaign: 'الحملة', advertiser: 'المعلن', listing: 'الإعلان الممول',
    placement: 'الموضع', targeting: 'الاستهداف', period: 'فترة الحملة',
    status: 'الحالة', impressions: 'مرات الظهور', clicks: 'النقرات', ctr: 'نسبة النقر',
    daily: 'التفصيل اليومي', date: 'التاريخ', none: '—',
    placements: { homepage: 'الصفحة الرئيسية', related_carousel: 'شريط الإعلانات المشابهة' },
    statuses: { active: 'نشطة', scheduled: 'مجدولة', paused: 'موقوفة', expired: 'مكتملة' },
    tzNote: 'جميع التواريخ بتوقيت السودان (Africa/Khartoum).',
    payNote: 'تمت تسوية الدفع خارج وصلة.',
    generated: 'تاريخ إنشاء التقرير',
    print: 'طباعة / حفظ PDF', copy: 'نسخ الملخص', back: 'رجوع',
    copied: 'تم نسخ الملخص', copyFail: 'فشل النسخ',
    noDaily: 'لا يوجد نشاط مسجّل لهذه الحملة بعد.',
  },
};

const fmtDate = (iso, lang) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(lang === 'ar' ? 'ar' : 'en-GB',
      { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Africa/Khartoum' });
  } catch { return iso; }
};

export default function SponsorshipReport() {
  const { sponsorshipId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { lang } = useLanguage();
  const isRTL = lang === 'ar';
  const t = T[isRTL ? 'ar' : 'en'];

  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/admin/sponsorships/${sponsorshipId}/report`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (res.ok) setReport(data.report);
        else setError(typeof data.detail === 'string' ? data.detail : 'Failed to load report');
      } catch {
        setError('Network error');
      }
    })();
  }, [sponsorshipId, token]);

  const copySummary = useCallback(async () => {
    if (!report) return;
    const r = report;
    const period = `${fmtDate(r.starts_at, lang)} → ${fmtDate(r.sponsored_until, lang)}`;
    const settledLineAr = r.payment_settled ? '\nتمت تسوية الدفع خارج وصلة.' : '';
    const settledLineEn = r.payment_settled ? '\nPayment settled outside Wassla.' : '';
    const text = isRTL
      ? `تقرير حملة وصلة الإعلانية\nالحملة: ${r.campaign_title}\nالمعلن: ${r.advertiser_name}\nالفترة: ${period} (توقيت السودان)\nالموضع: ${t.placements[r.placement] || r.placement}\nمرات الظهور: ${r.totals.impressions}\nالنقرات: ${r.totals.clicks}\nنسبة النقر: ${r.totals.ctr}%${settledLineAr}`
      : `Wassla Sponsored Campaign Report\nCampaign: ${r.campaign_title}\nAdvertiser: ${r.advertiser_name}\nPeriod: ${period} (Sudan time)\nPlacement: ${t.placements[r.placement] || r.placement}\nImpressions: ${r.totals.impressions}\nClicks: ${r.totals.clicks}\nCTR: ${r.totals.ctr}%${settledLineEn}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t.copied);
    } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = text; document.body.appendChild(ta); ta.select();
        document.execCommand('copy'); document.body.removeChild(ta);
        toast.success(t.copied);
      } catch { toast.error(t.copyFail); }
    }
  }, [report, isRTL, lang, t]);

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center text-sm text-slate-500" data-testid="report-error">
        {error}
      </div>
    );
  }
  if (!report) {
    return (
      <div className="min-h-screen grid place-items-center text-slate-400">
        <Loader2 className="animate-spin" size={22} />
      </div>
    );
  }

  const targeting = [report.targeting?.country, report.targeting?.city, report.targeting?.category]
    .filter(Boolean).join(' · ') || t.none;
  const BackIcon = isRTL ? ArrowRight : ArrowLeft;

  return (
    <div className="spn-report-shell" dir={isRTL ? 'rtl' : 'ltr'} data-testid="sponsorship-report-page">
      {/* Action bar — hidden in print */}
      <div className="spn-report-actions no-print">
        <button onClick={() => navigate('/admin/sponsorships')} className="spn-btn spn-btn-ghost" data-testid="report-back-btn">
          <BackIcon size={15} /> {t.back}
        </button>
        <div className="spn-actions-right">
          <button onClick={copySummary} className="spn-btn spn-btn-ghost" data-testid="report-copy-btn">
            <Copy size={15} /> {t.copy}
          </button>
          <button onClick={() => window.print()} className="spn-btn spn-btn-primary" data-testid="report-print-btn">
            <Printer size={15} /> {t.print}
          </button>
        </div>
      </div>

      {/* Printable report */}
      <div className="spn-report-sheet" data-testid="report-sheet">
        <header className="spn-report-head">
          <div className="spn-brand">
            <img src="/assets/wassla-logo.png" alt="Wassla" className="spn-brand-logo"
              onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            <div>
              <div className="spn-brand-name">Wassla · وصلة</div>
              <div className="spn-brand-sub">{t.brandSub}</div>
            </div>
          </div>
          <h1 className="spn-report-title">{t.title}</h1>
        </header>

        <section className="spn-meta-grid" data-testid="report-meta">
          <div className="spn-meta"><span>{t.campaign}</span><strong>{report.campaign_title}</strong></div>
          <div className="spn-meta"><span>{t.advertiser}</span><strong>{report.advertiser_name}</strong></div>
          <div className="spn-meta"><span>{t.placement}</span><strong>{t.placements[report.placement] || report.placement}</strong></div>
          <div className="spn-meta"><span>{t.targeting}</span><strong>{targeting}</strong></div>
          <div className="spn-meta">
            <span>{t.period}</span>
            <strong>{fmtDate(report.starts_at, lang)} → {fmtDate(report.sponsored_until, lang)}</strong>
          </div>
          <div className="spn-meta"><span>{t.status}</span><strong>{t.statuses[report.status] || report.status}</strong></div>
        </section>

        <section className="spn-kpis">
          <div className="spn-kpi" data-testid="report-kpi-impressions">
            <div className="spn-kpi-value">{report.totals.impressions.toLocaleString(isRTL ? 'ar-EG' : 'en')}</div>
            <div className="spn-kpi-label">{t.impressions}</div>
          </div>
          <div className="spn-kpi" data-testid="report-kpi-clicks">
            <div className="spn-kpi-value">{report.totals.clicks.toLocaleString(isRTL ? 'ar-EG' : 'en')}</div>
            <div className="spn-kpi-label">{t.clicks}</div>
          </div>
          <div className="spn-kpi spn-kpi-accent" data-testid="report-kpi-ctr">
            <div className="spn-kpi-value">{report.totals.ctr}%</div>
            <div className="spn-kpi-label">{t.ctr}</div>
          </div>
        </section>

        <section className="spn-daily">
          <h2>{t.daily}</h2>
          {report.daily.length === 0 ? (
            <p className="spn-empty" data-testid="report-daily-empty">{t.noDaily}</p>
          ) : (
            <table className="spn-table" data-testid="report-daily-table">
              <thead>
                <tr>
                  <th>{t.date}</th>
                  <th>{t.impressions}</th>
                  <th>{t.clicks}</th>
                  <th>{t.ctr}</th>
                </tr>
              </thead>
              <tbody>
                {report.daily.map((d) => (
                  <tr key={d.date}>
                    <td dir="ltr">{d.date}</td>
                    <td>{d.impressions}</td>
                    <td>{d.clicks}</td>
                    <td>{d.ctr}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <footer className="spn-report-foot">
          <p>{t.tzNote}</p>
          {report.payment_settled && <p data-testid="report-payment-note">{t.payNote}</p>}
          <p className="spn-generated">{t.generated}: {fmtDate(report.generated_at, lang)}</p>
        </footer>
      </div>

      <style>{`
        .spn-report-shell {
          min-height: 100vh; background: #eef1ee; padding: 16px;
          font-family: inherit;
        }
        .spn-report-actions {
          max-width: 760px; margin: 0 auto 12px;
          display: flex; justify-content: space-between; align-items: center; gap: 8px;
          flex-wrap: wrap;
        }
        .spn-actions-right { display: flex; gap: 8px; }
        .spn-btn {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 8px 14px; border-radius: 12px; border: none; cursor: pointer;
          font-size: 13px; font-weight: 600;
        }
        .spn-btn-ghost { background: #fff; color: #334155; border: 1px solid #e2e8f0; }
        .spn-btn-primary { background: #195B2F; color: #fff; }
        .spn-report-sheet {
          max-width: 760px; margin: 0 auto; background: #fff;
          border-radius: 18px; padding: 32px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.06);
        }
        .spn-report-head { border-bottom: 3px solid #195B2F; padding-bottom: 18px; margin-bottom: 20px; }
        .spn-brand { display: flex; align-items: center; gap: 12px; margin-bottom: 14px; }
        .spn-brand-logo { height: 40px; width: auto; }
        .spn-brand-name { font-size: 18px; font-weight: 800; color: #195B2F; }
        .spn-brand-sub { font-size: 12px; color: #64748b; }
        .spn-report-title { font-size: 22px; font-weight: 800; color: #0f172a; margin: 0; }
        .spn-meta-grid {
          display: grid; grid-template-columns: 1fr 1fr; gap: 10px 24px; margin-bottom: 22px;
        }
        .spn-meta { display: flex; flex-direction: column; gap: 2px; font-size: 13px; }
        .spn-meta span { color: #64748b; font-size: 11.5px; }
        .spn-meta strong { color: #0f172a; font-weight: 600; }
        .spn-kpis { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 24px; }
        .spn-kpi {
          background: #f6f8f6; border: 1px solid #e5eae5; border-radius: 14px;
          padding: 16px; text-align: center;
        }
        .spn-kpi-accent { background: #eaf4ec; border-color: #cfe5d4; }
        .spn-kpi-value { font-size: 26px; font-weight: 800; color: #195B2F; }
        .spn-kpi-label { font-size: 12px; color: #475569; margin-top: 4px; font-weight: 600; }
        .spn-daily h2 { font-size: 15px; font-weight: 700; color: #0f172a; margin: 0 0 10px; }
        .spn-empty { font-size: 13px; color: #64748b; }
        .spn-table { width: 100%; border-collapse: collapse; font-size: 13px; }
        .spn-table th {
          text-align: start; background: #195B2F; color: #fff;
          padding: 8px 12px; font-weight: 700; font-size: 12px;
        }
        .spn-table td { padding: 8px 12px; border-bottom: 1px solid #eef2ee; color: #1e293b; }
        .spn-table tbody tr:nth-child(even) { background: #fafcfa; }
        .spn-report-foot {
          margin-top: 26px; padding-top: 14px; border-top: 1px solid #e2e8f0;
          font-size: 11.5px; color: #64748b;
        }
        .spn-report-foot p { margin: 2px 0; }
        .spn-generated { color: #94a3b8; }
        @media (max-width: 520px) {
          .spn-report-sheet { padding: 20px; }
          .spn-meta-grid { grid-template-columns: 1fr; }
          .spn-kpis { grid-template-columns: 1fr 1fr 1fr; gap: 8px; }
          .spn-kpi-value { font-size: 20px; }
        }
        @media print {
          /* Coexist with CVBuilder.css global print rule (body * hidden):
             re-show ONLY the report sheet. */
          .spn-report-sheet, .spn-report-sheet * { visibility: visible !important; }
          .spn-report-sheet {
            position: absolute !important; left: 0 !important; top: 0 !important;
            width: 100% !important; max-width: 100% !important;
            box-shadow: none !important; border-radius: 0 !important;
            padding: 12mm !important; margin: 0 !important;
          }
          .no-print, header.header, nav.bnav, .bnav, .home-fab, [data-testid="bottom-nav"] {
            display: none !important; visibility: hidden !important;
          }
          .spn-report-shell { background: #fff !important; padding: 0 !important; }
        }
      `}</style>
    </div>
  );
}
